const API_BASE_URL = 'https://leadapi.crmbytes.com/api';
const GEO_API_URL = 'https://validationapi.thetechmenders.com/api';

/**
 * Fetch geolocation data from API
 * @returns {Promise<Object>} Geolocation data
 */
export const getGeoLocation = async () => {
  try {
    const response = await fetch(`${GEO_API_URL}/GeoLocation/GetGeoLocation`, {
      method: 'GET',
      headers: {
        'accept': 'text/plain'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch geolocation');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Geolocation API Error:', error);
    return null;
  }
};

/**
 * Validate email format
 * @param {string} email - Email address
 * @returns {boolean} Is valid email
 */
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate phone number (should be numeric)
 * @param {string} phone - Phone number
 * @returns {boolean} Is valid phone
 */
export const validatePhone = (phone) => {
  const phoneRegex = /^\d{7,15}$/; // Between 7 to 15 digits
  return phoneRegex.test(phone);
};

/**
 * Submit lead form data to API
 * @param {Object} formData - Form data object
 * @param {string} formData.name - Customer name
 * @param {string} formData.email - Customer email
 * @param {string} formData.phoneNumber - Phone number (numeric only)
 * @param {string} formData.countryDialCode - Country dial code
 * @param {string} formData.country - Country code
 * @param {string} formData.description - Service description
 * @param {string} formData.packageTitle - Package title (optional)
 * @param {number} formData.packageAmount - Package amount (optional)
 * @returns {Promise<Object>} API response
 */
export const submitLead = async (formData) => {
  // Validate email
  if (!validateEmail(formData.email)) {
    return {
      success: false,
      error: 'Please enter a valid email address',
      statusCode: 400
    };
  }

  // Validate phone
  if (!validatePhone(formData.phoneNumber)) {
    return {
      success: false,
      error: 'Please enter a valid phone number (7-15 digits)',
      statusCode: 400
    };
  }
  try {
    // Get geolocation data
    const geoData = await getGeoLocation();
    
    // Extract city and state from geolocation if available
    const city = geoData?.data?.cityName || '';
    const state = geoData?.data?.regionName || '';
    const ipAddress = geoData?.data?.ipAddress || '';
    
    // Get current page URL
    const pageUrl = window.location.href;

    // Prepare payload
    const payload = {
      name: formData.name,
      email: formData.email,
      countryDialCode: formData.countryDialCode || 1,
      country: formData.country || 'US',
      phoneNumber: formData.phoneNumber, // Numeric only, no dial code
      ipAddress: ipAddress,
      description: formData.description || '',
      pageUrl: pageUrl,
      city: city,
      state: state,
      geoLocation: geoData ? JSON.stringify(geoData) : '',
      packageTitle: formData.packageTitle || '',
      packageAmount: formData.packageAmount || 0
    };

    // Make API call
    const response = await fetch(`${API_BASE_URL}/Lead/AddLead`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'accept': 'text/plain'
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json();

    // Handle different status codes
    if (response.status === 200 && data.isSuccess) {
      return {
        success: true,
        customerId: data.data.customerId,
        message: data.data.message,
        data: data
      };
    } else if (response.status === 401) {
      return {
        success: false,
        error: data.message || 'Unauthorized',
        statusCode: 401
      };
    } else if (response.status === 400) {
      return {
        success: false,
        error: data.errorMessage || 'Bad Request',
        statusCode: 400
      };
    } else {
      return {
        success: false,
        error: 'Something went wrong. Please try again.',
        statusCode: response.status
      };
    }
  } catch (error) {
    console.error('Submit Lead API Error:', error);
    return {
      success: false,
      error: 'Network error. Please check your connection and try again.',
      statusCode: 500
    };
  }
};

/**
 * Redirect to application page with customer ID
 * @param {number} customerId - Customer ID from API response
 */
export const redirectToApplication = (customerId) => {
  window.location.href = `/application/main?customerid=${customerId}`;
};
